import React, { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Building2, ChevronDown } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const OutletSwitcher = () => {
  const { user, token, updateToken } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [switching, setSwitching] = useState(false);

  if (!user || !user.outlet_roles || user.outlet_roles.length === 0) {
    return null;
  }

  // Get unique outlets from outlet_roles
  const outlets = user.outlet_roles.reduce((acc, role) => {
    if (!acc.find(o => o.outlet_id === role.outlet_id)) {
      acc.push(role);
    }
    return acc;
  }, []);

  // If only one outlet, don't show switcher
  if (outlets.length <= 1) {
    return null;
  }

  const currentOutlet = user.current_outlet_id;

  const handleSwitchOutlet = async (outletId) => {
    if (outletId === currentOutlet || switching) return;

    setSwitching(true);
    setIsOpen(false);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/switch-outlet`,
        { current_outlet_id: outletId },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const { access_token } = response.data;
      updateToken(access_token);
      
      // Reload page to refresh data for new outlet
      window.location.reload();
    } catch (error) {
      console.error('Error switching outlet:', error);
      alert('Failed to switch outlet. Please try again.');
    } finally {
      setSwitching(false);
    }
  };

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        disabled={switching}
        className="flex items-center gap-2 px-3 py-2 rounded-lg bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors disabled:opacity-50"
      >
        <Building2 size={18} className="text-blue-600 dark:text-blue-400" />
        <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
          {switching ? 'Switching...' : outlets.find(o => o.outlet_id === currentOutlet)?.outlet_id?.slice(0, 8) || 'Current Outlet'}
        </span>
        <ChevronDown size={16} className="text-gray-500" />
      </button>

      {isOpen && (
        <div className="absolute top-full mt-2 right-0 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 py-2 min-w-[200px] z-50">
          <div className="px-3 py-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
            Switch Outlet
          </div>
          {outlets.map((outlet) => (
            <button
              key={outlet.outlet_id}
              onClick={() => handleSwitchOutlet(outlet.outlet_id)}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors ${
                outlet.outlet_id === currentOutlet
                  ? 'bg-blue-50 dark:bg-blue-900/20 text-blue-600 dark:text-blue-400 font-medium'
                  : 'text-gray-700 dark:text-gray-300'
              }`}
            >
              <div className="flex items-center justify-between">
                <span>{outlet.outlet_id.slice(0, 8)}...</span>
                {outlet.outlet_id === currentOutlet && (
                  <span className="text-xs">✓</span>
                )}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                {outlet.role_code}
              </div>
            </button>
          ))}
        </div>
      )}

      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
};

export default OutletSwitcher;
